import {FormEvent, ReactNode} from 'react';
import KindPill from './KindPill';
import PlantField from './PlantField';
import WhenRow from './WhenRow';
import WhereChips from './WhereChips';
import ErrorBox, {FieldError, isGeneralError} from '../feedback/ErrorBox';
import Icon from '../Icon';
import {KIND_DETAILS} from '../../lib/kinds';
import {FollowUp, followUpsFor, inDays} from '../../lib/todos';
import {Kind} from '../../types';
import Button from '../Button';

// Step 3 of a new tell and the edit screen (design/screens/Tell3Words.dc.html): header with
// Back, the kind pill (tap to change) and Save; then photos, the big text field, Plant, Where,
// When, and "More, if you like" below the fold.

export interface TellFormValues {
    kind: Kind;
    detail: string | null;
    text: string;
    plants: string[];
    placeId: string | null;
    whenLocal: string;
    quantity: string;
    /** "Makes me happy": a flag on any kind. */
    happy: boolean;
    /** "Remind me": a to-do made after the tell saves, following it. */
    followUp: FollowUp | null;
}

type Props = {
    values: TellFormValues;
    onChange: (patch: Partial<TellFormValues>) => void;
    onBack: () => void;
    onChangeKind: () => void;
    onSubmit: () => void;
    submitting: boolean;
    error: unknown;
    submitLabel?: string;
    photosSlot?: ReactNode;
    footer?: ReactNode;
    plantHint?: string | null;
};

export default function TellForm({values, onChange, onBack, onChangeKind, onSubmit, submitting, error, submitLabel = 'Save', photosSlot, footer, plantHint}: Props) {
    const submit = (event: FormEvent) => {
        event.preventDefault();
        if (!submitting) onSubmit();
    };

    return (
        <form onSubmit={submit} className="settle-in flex min-h-dvh flex-col">
            <header className="flex items-center justify-between gap-2 border-b border-line py-3 pl-3 pr-4">
                <button type="button" onClick={onBack} aria-label="Back" className="flex h-11 w-11 items-center justify-center rounded-pill">
                    <Icon name="back" size={22} />
                </button>
                <button type="button" onClick={onChangeKind} className="flex items-center gap-1" aria-label="Change kind">
                    <KindPill kind={values.kind} size="lg" />
                    <Icon name="down" size={14} className="-ml-1 text-ink-2" />
                </button>
                <Button type="submit" busy={submitting} className="min-h-[44px] rounded-pill px-5 text-[15px] font-bold">
                    {submitLabel}
                </Button>
            </header>

            <div className="flex flex-1 flex-col gap-5 px-4 pb-10 pt-[18px]">
                {(KIND_DETAILS[values.kind]?.length ?? 0) > 0 && (
                    <div className="-mx-4 flex gap-2 overflow-x-auto px-4 py-1 [scrollbar-width:none]" role="group" aria-label="Which">
                        {KIND_DETAILS[values.kind]!.map(d => (
                            <button key={d} type="button" className="chip" aria-pressed={values.detail === d} onClick={() => onChange({detail: values.detail === d ? null : d})}>
                                {d}
                            </button>
                        ))}
                    </div>
                )}

                {photosSlot}

                <label className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-ink-2">
                        What happened? <span className="font-medium">The first line becomes the title.</span>
                    </span>
                    <textarea
                        className="min-h-[96px] w-full resize-y bg-transparent font-display text-[21px] font-semibold leading-[1.3] tracking-[-0.01em] outline-none placeholder:font-normal placeholder:text-ink-3"
                        rows={3}
                        maxLength={4000}
                        placeholder="Six Moneymaker seedlings in…"
                        value={values.text}
                        onChange={e => onChange({text: e.target.value})}
                        autoFocus={!values.text}
                    />
                    <FieldError error={error} field="text" />
                </label>

                <PlantField values={values.plants} onChange={plants => onChange({plants})} hint={plantHint} />

                <WhereChips value={values.placeId} onChange={placeId => onChange({placeId})} />
                <FieldError error={error} field="placeId" />

                <WhenRow value={values.whenLocal} onChange={whenLocal => onChange({whenLocal})} />
                <FieldError error={error} field="whenLocal" />

                <section className="flex flex-col gap-2">
                    <span className="band-label text-ink-2">More, if you like</span>
                    <div className="rows rounded-md border-[1.5px] border-line bg-surface">
                        <label className="flex min-h-[52px] items-center gap-3 px-4">
                            <Icon name="hash" size={20} className="text-ink-2" />
                            <span className="text-sm text-ink-2">How many</span>
                            <input
                                type="text"
                                className="min-w-0 flex-1 bg-transparent text-right font-semibold outline-none placeholder:font-normal placeholder:text-ink-3"
                                placeholder="6 seedlings, 1.2 kg…"
                                maxLength={60}
                                value={values.quantity}
                                onChange={e => onChange({quantity: e.target.value})}
                            />
                        </label>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={values.happy}
                            className="flex min-h-[52px] w-full items-center gap-3 px-4 text-left"
                            onClick={() => onChange({happy: !values.happy})}
                        >
                            <Icon name="happy" size={20} className="text-marigold-text" />
                            <span className="flex-1 font-semibold">Makes me happy</span>
                            <span
                                aria-hidden
                                className={`flex h-7 w-12 items-center rounded-pill p-[3px] transition-colors ${values.happy ? 'justify-end bg-marigold' : 'justify-start bg-line'}`}
                            >
                                <span className="h-[22px] w-[22px] rounded-pill bg-surface shadow-1" />
                            </span>
                        </button>
                        <div className="flex flex-col gap-2 px-4 py-3">
                            <span className="flex items-center gap-3">
                                <Icon name="reminder" size={20} className="text-marigold-text" />
                                <span className="flex-1 font-semibold">Remind me</span>
                            </span>
                            <div className="flex flex-wrap gap-2 pl-8" role="group" aria-label="Remind me">
                                {followUpsFor(values.kind, values.detail).map(f => {
                                    const on = values.followUp?.text === f.text && values.followUp.days === f.days;
                                    return (
                                        <button key={`${f.text}${f.days}`} type="button" className="chip" aria-pressed={on} onClick={() => onChange({followUp: on ? null : f})}>
                                            {f.text} · {inDays(f.days)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <FieldError error={error} field="quantity" />
                </section>

                {isGeneralError(error) && <ErrorBox error={error} />}
                {footer}
            </div>
        </form>
    );
}
